import { useState, useEffect, useCallback } from 'react';
import toast from '../utils/toast';

/**
 * Custom hook for API calls with loading, error, and success states
 */
export const useApi = (apiFunction, dependencies = [], options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const {
    immediate = true,
    showToast = false,
    successMessage = 'Operation successful',
    errorMessage = 'Operation failed'
  } = options;

  const execute = useCallback(async (...args) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await apiFunction(...args);
      setData(result);
      
      if (showToast) {
        toast.success(successMessage);
      }
      
      return result;
    } catch (err) {
      const errorMsg = err.message || errorMessage;
      setError(errorMsg);
      
      if (showToast) {
        toast.error(errorMsg);
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiFunction, showToast, successMessage, errorMessage]);

  useEffect(() => {
    if (immediate && apiFunction) {
      execute();
    }
  }, dependencies);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    data,
    loading,
    error,
    execute,
    reset,
    refetch: execute
  };
};

/**
 * Hook for API calls that need manual triggering (like form submissions)
 */
export const useAsyncAction = (apiFunction, options = {}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const {
    showToast = true,
    successMessage = 'Operation successful',
    errorMessage = 'Operation failed'
  } = options;

  const execute = useCallback(async (...args) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await apiFunction(...args);
      
      if (showToast) {
        toast.success(successMessage);
      }
      
      return result;
    } catch (err) {
      const errorMsg = err.message || errorMessage;
      setError(errorMsg);
      
      if (showToast) {
        toast.error(errorMsg);
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiFunction, showToast, successMessage, errorMessage]);

  const reset = useCallback(() => {
    setError(null);
    setLoading(false);
  }, []);

  return {
    loading,
    error,
    execute,
    reset
  };
};

/**
 * Hook for paginated API calls
 */
export const usePaginatedApi = (apiFunction, options = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  
  const { pageSize = 10, showToast = false } = options;

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const result = await apiFunction(page, pageSize);
      
      if (result.data && Array.isArray(result.data)) {
        setData(prev => page === 1 ? result.data : [...prev, ...result.data]);
        setHasMore(result.hasMore || result.data.length === pageSize);
        setPage(prev => prev + 1);
      }
      
      return result;
    } catch (err) {
      const errorMsg = err.message || 'Failed to load data';
      setError(errorMsg);
      
      if (showToast) {
        toast.error(errorMsg);
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiFunction, page, pageSize, loading, hasMore, showToast]);

  const refresh = useCallback(() => {
    setData([]);
    setPage(1);
    setHasMore(true);
    setError(null);
  }, []);

  useEffect(() => {
    loadMore();
  }, []);

  return {
    data,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
    page: page - 1 // Return 0-based page for display
  };
};

export default useApi;